import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/App";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Anchor, WarningCircle } from "@phosphor-icons/react";

const BG_IMAGE = "https://images.unsplash.com/photo-1724364552281-dbed323c4633?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHBvcnQlMjBjb250YWluZXIlMjBzaGlwJTIwYWVyaWFsfGVufDB8fHx8MTc3NjEwNzM5N3ww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(formatApiErrorDetail(err.response.data.detail));
      } else if (err.message === "Network Error") {
        setError("Cannot connect to server. Please check your connection and try again.");
      } else {
        setError(err.message || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      {/* Left panel - login form */}
      <div className="flex flex-col justify-center w-full lg:w-[480px] px-8 md:px-12 lg:px-16 bg-white">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <div className="mb-10">
            <img
              src="https://www.cargo-cfs.com/wp-content/uploads/2020/08/prenos.jpg"
              alt="Cargo CFS"
              className="h-10 object-contain"
              data-testid="login-logo"
            />
          </div>

          <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Welcome back
          </h1>
          <p className="text-sm text-slate-500 mb-8">Sign in to access the reporting dashboard</p>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-testid="login-error">
              <WarningCircle size={18} weight="fill" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="consulting@cargo-cfs.com"
                className="h-11 border-slate-300 focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-11 border-slate-300 focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                required
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-blue-900 hover:bg-blue-800 text-white font-semibold transition-colors duration-200 rounded-md"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200 flex items-center gap-2 text-xs text-slate-400">
            <Anchor size={14} />
            <span>CARGO CFS d.o.o. | Koper, Slovenia</span>
          </div>
        </div>
      </div>

      {/* Right panel - background image */}
      <div className="hidden lg:block flex-1 relative">
        <img
          src={BG_IMAGE}
          alt="Cargo port"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-blue-950/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="text-3xl font-black text-white tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Survey & Reporting System
          </h2>
          <p className="text-blue-100/80 text-sm max-w-md">
            Generate professional cargo securing, labeling, and label removal reports with ease.
          </p>
        </div>
      </div>
    </div>
  );
}
